/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/platform/design-system/components/ui/Card.tsx
 * layer: web
 * area: components
 * kind: component
 * web_root: components
 * relpath: web/src/platform/design-system/components/ui/Card.tsx
 * filename: Card.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

import { HTMLAttributes } from 'react';
import { clsx } from 'clsx';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'interactive' | 'accent';
  accentColor?: 'amber' | 'purple' | 'green' | 'cyan' | 'brand';
}

const accentBorderColors = {
  amber: 'border-l-[#D97706]',
  purple: 'border-l-[#7C3AED]',
  green: 'border-l-[#059669]',
  cyan: 'border-l-[#0891B2]',
  brand: 'border-l-[#2563EB]',
};

export const Card = (
  { className, variant = 'default', accentColor, ref, ...props }: CardProps & { ref?: React.Ref<HTMLDivElement> }
) => (
  <div
    ref={ref}
    className={clsx(
      'rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-bg-secondary))]',
      variant === 'interactive' &&
        'hover-lift cursor-pointer transition-[border-color,box-shadow,transform] duration-[var(--motion-duration-feedback)] hover:shadow-md hover:border-[rgb(var(--color-border-hover))] motion-reduce:transform-none motion-reduce:transition-none',
      variant === 'accent' && accentColor && ['border-l-[3px]', accentBorderColors[accentColor]],
      className
    )}
    {...props}
  />
);

export const CardHeader = (
  { className, ref, ...props }: HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }
) => (
  <div
    ref={ref}
    className={clsx('flex flex-col space-y-1.5 p-6', className)}
    {...props}
  />
);

export const CardTitle = (
  { className, children, ref, ...props }: HTMLAttributes<HTMLHeadingElement> & { ref?: React.Ref<HTMLHeadingElement> }
) => (
  <h3
    ref={ref}
    className={clsx('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  >
    {children}
  </h3>
);

export const CardDescription = (
  { className, ref, ...props }: HTMLAttributes<HTMLParagraphElement> & { ref?: React.Ref<HTMLParagraphElement> }
) => (
  <p
    ref={ref}
    className={clsx('text-sm text-[rgb(var(--color-text-secondary))]', className)}
    {...props}
  />
);

export const CardContent = (
  { className, ref, ...props }: HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }
) => (
  <div ref={ref} className={clsx('p-6 pt-0', className)} {...props} />
);

export const CardFooter = (
  { className, ref, ...props }: HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }
) => (
  <div
    ref={ref}
    className={clsx('flex items-center p-6 pt-0', className)}
    {...props}
  />
);
